/**
 * Why a patch was refused (PLAN §6.4: "Rejected patches get a nack, and the
 * client rolls back its optimistic change").
 *
 * One vocabulary for both refusal points — `validatePatch` (schema, parent
 * rules, permission) and `applyPatch` (identity, creation, ownership, text) —
 * because the client handles a nack the same way whichever layer produced it.
 * The wire nack (`@acp/api-contract`) carries these codes verbatim.
 */
import { z } from 'zod';

export const PATCH_REJECTION_CODES = [
  /** A non-creation patch for a row this replica does not hold. Replication recovers via `changedSince`. */
  'unknown-row',
  /** A creation patch without `set.parentId` and `set.ord`. */
  'incomplete-create',
  /** `create` disagrees with the existing row's kind or rundown. */
  'create-mismatch',
  /** `create` is malformed for its kind (a rundown or playlist whose `rundownId` is not its own `rowId`). */
  'invalid-create',
  /** `create.source` names a system other than the writer's. */
  'source-mismatch',
  /** The patch's `rowId` is not the row it was applied to. */
  'row-mismatch',
  /** The row lives in a different rundown than the request targets. */
  'rundown-mismatch',
  /** A locked field (D2) written by a system that does not own the row. */
  'locked-field',
  /** A text field written through `set` instead of `text`. */
  'text-field-set',
  /** A `text` entry for a field that is not a text field of this kind. */
  'unknown-text-field',
  /** A Yjs update that does not decode or apply. */
  'invalid-text-update',
  /** A field this kind does not have (rejected at client ingress; §6.8). */
  'unknown-field',
  /** A value that fails the field's schema. */
  'invalid-value',
  /** A parent that is not allowed: null for a non-root kind, non-null for a playlist. */
  'invalid-parent',
  /** The named parent row does not exist. */
  'parent-not-found',
  /** The parent's kind is not allowed for this kind (PLAN §6.4: a piece must hang under a part). */
  'parent-kind',
  /** The parent lives in another rundown. */
  'parent-rundown',
  /** The HLC is malformed. */
  'invalid-hlc',
  /** A field path is malformed. */
  'invalid-field-path',
  /** The caller lacks the permission. */
  'forbidden',
] as const;

export const patchRejectionCodeSchema = z.enum(PATCH_REJECTION_CODES);
export type PatchRejectionCode = z.infer<typeof patchRejectionCodeSchema>;

export const patchRejectionSchema = z
  .object({
    code: patchRejectionCodeSchema,
    /** The field path concerned, or null for a whole-patch reason. */
    field: z.string().nullable(),
    message: z.string(),
  })
  .strict();
export type PatchRejection = z.infer<typeof patchRejectionSchema>;

export function rejection(code: PatchRejectionCode, field: string | null, message: string): PatchRejection {
  return { code, field, message };
}
